import { Link, Navigate, Outlet } from '@tanstack/react-router';
import { Button } from '@tatra/ui';
import { Menu, X } from 'lucide-react';
import { useState } from 'react';
import { Brand, Loading } from './components.js';
import { useSession } from './lib/session.js';

// The header is the brand's black band in both themes (D-123).
const linkClass =
  'flex min-h-11 items-center rounded-lg px-3 text-sm font-medium text-nav-muted transition-colors hover:bg-nav-hover hover:text-nav-text md:min-h-9';
const activeClass = { className: `${linkClass} bg-nav-active-bg !text-nav-text` };

/** Signed-in frame. Signed-out visitors go to the sign-in screen. */
export function Shell() {
  const { state, me, isOwner, approved, signOut, config } = useSession();
  const money = config?.money !== false;
  const [open, setOpen] = useState(false);
  if (state === 'loading') return <Loading label="Opening your portal" />;
  if (state === 'out') return <Navigate to="/sign-in" />;

  const items: { to: string; label: string; show: boolean; exact?: boolean }[] = [
    { to: '/', label: 'Home', show: true, exact: true },
    { to: '/register', label: 'Register lead', show: approved },
    { to: '/leads', label: 'My leads', show: true },
    { to: '/commissions', label: 'Commissions', show: money },
    { to: '/claims', label: 'Claims', show: isOwner && money },
    { to: '/team', label: 'Team', show: isOwner },
    { to: '/profile', label: approved ? 'Profile' : 'Your details', show: true },
  ];

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 bg-nav-bg text-nav-text">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center gap-x-4 px-4 py-2.5">
          <Link to="/" className="min-w-0 flex-1 md:flex-none">
            <Brand onDark />
          </Link>
          <Button
            variant="ghost"
            size="icon"
            className="text-nav-muted hover:bg-nav-hover hover:text-nav-text md:hidden"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
            aria-controls="portal-nav"
            onClick={() => setOpen(!open)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <nav
            id="portal-nav"
            aria-label="Portal"
            className={`${open ? 'flex' : 'hidden'} w-full flex-col gap-1 pb-2 md:flex md:w-auto md:flex-1 md:flex-row md:pb-0`}
          >
            {items
              .filter((i) => i.show)
              .map((i) => (
                <Link
                  key={i.to}
                  to={i.to}
                  activeOptions={{ exact: !!i.exact }}
                  activeProps={activeClass}
                  className={linkClass}
                  onClick={() => setOpen(false)}
                >
                  {i.label}
                </Link>
              ))}
            <span className="hidden flex-1 md:block" />
            <span className="px-3 text-xs text-nav-faint md:flex md:items-center">{me?.me.name}</span>
            <Button
              variant="ghost"
              className="border border-nav-border text-nav-text hover:bg-nav-hover hover:text-nav-text"
              onClick={() => void signOut()}
            >
              Sign out
            </Button>
          </nav>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-4 py-6 sm:py-8">
        <Outlet />
      </main>
    </div>
  );
}
